import {
  confirmPasswordReset,
  ensureFreshSession,
  onAuthStateChange,
  requestPasswordReset,
  signInWithMagicLink,
  signInWithPassword,
  signUpWithPassword,
} from '@bangle.io/supabase';
import { TypoCat } from '@bangle.io/ui-components';
import React, { useEffect, useMemo, useState } from 'react';

import {
  clearAuthRecoveryFromUrl,
  pocketBaseResetTokenFromHash,
  pocketBaseResetTokenFromSearch,
} from './auth-recovery';

type AuthMode = 'signin' | 'signup' | 'magic' | 'reset' | 'recovery';

function nextUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('next') || '/';
}

function submitLabel(mode: AuthMode, busy: boolean): string {
  if (busy) return t.app.pageAuth.submitBusy;
  if (mode === 'signup') return t.app.pageAuth.submitSignUp;
  if (mode === 'magic') return t.app.pageAuth.submitMagic;
  if (mode === 'reset') return t.app.pageAuth.submitReset;
  if (mode === 'recovery') return t.app.pageAuth.submitRecovery;
  return t.app.pageAuth.submitSignIn;
}

function readResetToken(): string | null {
  return (
    pocketBaseResetTokenFromHash() || pocketBaseResetTokenFromSearch() || null
  );
}

export function LoginPage() {
  const initialToken = useMemo(() => readResetToken(), []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(initialToken);
  const [mode, setMode] = useState<AuthMode>(
    initialToken ? 'recovery' : 'signin',
  );
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const redirectTo = useMemo(() => nextUrl(), []);

  useEffect(() => {
    const token = readResetToken();
    if (token) {
      setResetToken(token);
      setMode('recovery');
    }
  }, []);

  useEffect(() => {
    if (mode === 'recovery') return;
    void ensureFreshSession().then((session) => {
      if (session) window.location.replace(redirectTo);
    });
    return onAuthStateChange((session) => {
      if (session) window.location.replace(redirectTo);
    });
  }, [redirectTo, mode]);

  function switchMode(next: AuthMode) {
    setError(null);
    setMessage(null);
    setMode(next);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (mode === 'recovery') {
        if (!resetToken) {
          throw new Error(
            'Reset link is missing or expired. Request a new one from Forgot password.',
          );
        }
        if (password !== passwordConfirm) {
          throw new Error('Passwords do not match.');
        }
        await confirmPasswordReset(resetToken, password, passwordConfirm);
        clearAuthRecoveryFromUrl();
        setResetToken(null);
        setPassword('');
        setPasswordConfirm('');
        setMessage(t.app.pageAuth.recoverySaved);
        setMode('signin');
      } else if (mode === 'reset') {
        await requestPasswordReset(email);
        setMessage(t.app.pageAuth.resetSent);
      } else if (mode === 'magic') {
        await signInWithMagicLink(email);
        setMessage(t.app.pageAuth.magicSent);
      } else if (mode === 'signup') {
        await signUpWithPassword(email, password);
        setMessage(t.app.pageAuth.signupCreated);
        setMode('signin');
      } else {
        await signInWithPassword(email, password);
        window.location.replace(redirectTo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.app.pageAuth.authFailed);
    } finally {
      setBusy(false);
    }
  }

  const showModeTabs =
    mode === 'signin' || mode === 'signup' || mode === 'magic';

  return (
    <div className="sn-auth-shell">
      <div className="sn-auth-stage">
        <section className="sn-auth-hero">
          <h1 className="sn-auth-brand">{t.app.pageAuth.brand}</h1>
          <span className="sn-auth-badge">{t.app.common.earlyAlpha}</span>
          <p className="sn-auth-sub">{t.app.pageAuth.subtitle}</p>
          <TypoCat pose="with-a-letter" className="sn-auth-mascot" decorative />
        </section>

        <section className="sn-auth-panel" aria-label={t.app.pageAuth.brand}>
          {showModeTabs && (
            <div
              className="sn-auth-modes"
              role="tablist"
              aria-label={t.app.pageAuth.modeGroupLabel}
            >
              {(
                [
                  ['signin', t.app.pageAuth.modePassword],
                  ['magic', t.app.pageAuth.modeMagic],
                  ['signup', t.app.pageAuth.modeSignup],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={mode === id}
                  className={mode === id ? 'is-active' : undefined}
                  onClick={() => switchMode(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {mode === 'reset' && (
            <p className="sn-auth-sub" style={{ marginBottom: '0.75rem' }}>
              Enter your email and we’ll send a reset link.
            </p>
          )}
          {mode === 'recovery' && (
            <p className="sn-auth-sub" style={{ marginBottom: '0.75rem' }}>
              Choose a new password for your account.
            </p>
          )}

          <form className="sn-auth-form" onSubmit={submit}>
            {mode !== 'recovery' && (
              <label>
                {t.app.pageAuth.emailLabel}
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                />
              </label>
            )}
            {(mode === 'signin' ||
              mode === 'signup' ||
              mode === 'recovery') && (
              <label>
                <span className="sn-auth-label-row">
                  <span>{t.app.pageAuth.passwordLabel}</span>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      className="sn-auth-forgot"
                      onClick={() => switchMode('reset')}
                    >
                      {t.app.pageAuth.forgotPassword || 'Forgot password?'}
                    </button>
                  )}
                </span>
                <input
                  type="password"
                  autoComplete={
                    mode === 'signin' ? 'current-password' : 'new-password'
                  }
                  required
                  minLength={8}
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                />
              </label>
            )}
            {mode === 'recovery' && (
              <label>
                {t.app.pageAuth.passwordConfirmLabel}
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={passwordConfirm}
                  onChange={(ev) => setPasswordConfirm(ev.target.value)}
                />
              </label>
            )}
            {error && (
              <p className="sn-auth-error" role="alert">
                {error}
              </p>
            )}
            {message && (
              <p className="sn-auth-ok" role="status">
                {message}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {submitLabel(mode, busy)}
            </button>
          </form>

          {(mode === 'reset' || mode === 'recovery') && (
            <button
              type="button"
              className="sn-auth-link"
              onClick={() => {
                if (mode === 'recovery') {
                  clearAuthRecoveryFromUrl();
                  setResetToken(null);
                }
                switchMode('signin');
              }}
            >
              {t.app.pageAuth.backToSignIn || 'Back to sign in'}
            </button>
          )}

          {showModeTabs && (
            <a className="sn-auth-link" href="/">
              {t.app.pageAuth.continueLocal}
            </a>
          )}
        </section>
      </div>
    </div>
  );
}
